import { useSyncExternalStore } from 'react'

/**
 * Nguồn sự thật DUY NHẤT cho câu hỏi "đang ở giao diện mobile hay desktop".
 *
 * Trước đây có hai câu trả lời khác nhau: khung layout đổi tại `lg:` (1024px)
 * còn `useIsMobile()` đổi tại 767px. Khoảng 768–1023px vì thế rơi vào trạng
 * thái nửa vời — có thanh tab đáy của mobile nhưng bảng vẫn vẽ kiểu desktop.
 * iPad dọc là 768×1024, tức là rơi đúng vào đó.
 *
 * Quy tắc mới nhận diện theo CẢM ỨNG chứ không chỉ theo bề rộng, để mọi iPad
 * đều vào giao diện mobile kể cả khi xoay ngang:
 *
 *   - `(pointer: coarse) and (max-width: 1366px)` — thiết bị cảm ứng cỡ máy
 *     tính bảng trở xuống. Trần 1366px là iPad Pro 12.9" nằm ngang.
 *   - `(max-width: 1023.98px)` — cửa sổ hẹp trên máy tính để bàn vẫn ra mobile
 *     dù con trỏ là chuột.
 *
 * Hệ quả phải biết: `pointer: coarse` cũng đúng với LAPTOP MÀN CẢM ỨNG. Vì vậy
 * `setViewportOverride()` là bắt buộc phải có, không phải tính năng phụ —
 * thiếu nó thì người dùng laptop cảm ứng kẹt trong giao diện điện thoại.
 *
 * Trạng thái được ghi ra `data-viewport` trên thẻ <html> để CSS đọc được
 * (variant `mobile:` / `desktop:` khai báo trong tailwind.config.js). Lý do
 * không dùng breakpoint Tailwind: breakpoint thuần bề rộng, không diễn đạt
 * được `pointer: coarse`.
 */

export type ViewportMode = 'mobile' | 'desktop'

const MOBILE_QUERY =
  '(pointer: coarse) and (max-width: 1366px), (max-width: 1023.98px)'

const STORAGE_KEY = 'shoptrack:viewport'

function readOverride(): ViewportMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'mobile' || v === 'desktop' ? v : null
  } catch {
    // Chế độ riêng tư hoặc bị chặn lưu trữ: coi như không có lựa chọn thủ công.
    return null
  }
}

let override: ViewportMode | null = null
let current: ViewportMode = 'desktop'
const listeners = new Set<() => void>()

function detect(): ViewportMode {
  if (override) return override
  if (typeof window === 'undefined') return 'desktop'
  return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop'
}

function apply(): void {
  const next = detect()
  if (next === current) return
  current = next
  document.documentElement.setAttribute('data-viewport', next)
  listeners.forEach((fn) => fn())
}

// Chạy ngay lúc nạp module, trước khi React render lần đầu: nếu đặt thuộc tính
// trong useEffect thì khung sẽ nháy một nhịp giao diện desktop rồi mới đổi.
if (typeof window !== 'undefined') {
  override = readOverride()
  current = detect()
  document.documentElement.setAttribute('data-viewport', current)
  window.matchMedia(MOBILE_QUERY).addEventListener('change', apply)
}

/** Chế độ đang hiển thị, đã tính cả lựa chọn thủ công. */
export function useViewportMode(): ViewportMode {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange)
      return () => {
        listeners.delete(onChange)
      }
    },
    () => current,
    () => 'desktop' as ViewportMode,
  )
}

/** Lựa chọn thủ công đang có, hoặc null nếu đang để tự động nhận diện. */
export function useViewportOverride(): ViewportMode | null {
  useViewportMode() // đăng ký nhận thay đổi
  return override
}

/** Đặt `null` để quay về tự động nhận diện. */
export function setViewportOverride(mode: ViewportMode | null): void {
  override = mode
  try {
    if (mode) localStorage.setItem(STORAGE_KEY, mode)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Không lưu được thì lựa chọn chỉ sống trong phiên này — vẫn hơn là không đổi.
  }
  apply()
  // Khi người dùng tự chọn mà chế độ trùng với chế độ đang hiển thị, `apply()`
  // không báo gì cả. Vẫn phải đánh thức để nút đổi nhãn.
  listeners.forEach((fn) => fn())
}
